import CredReqEventStatus from '$shared/constants/CredReqEventStatus';
import dayjs from 'dayjs';
import type { CommonList } from '../CommonList';
import { CredReqEventStatusExtension } from '../extensions/CredReqEventStatusExtension';
import { DateFormat } from '$shared/constants/Date';
import CredReqType from '$shared/constants/CredReqType';

export abstract class CvoStatusUpdateFormModel {
  public readonly hasEntityHoldNextCredDate: boolean;
  public readonly hasNoMonitorEnabled: boolean;
  public readonly credType: CredReqType;

  public note = '';
  public status: CredReqEventStatus;
  public disableAllMonitorCheck: boolean;
  public nextCredentialingDate: string | null;
  public removeNextCredDateCheck: boolean;

  constructor(
    hasEntityHoldNextCredDate: boolean,
    hasNoMonitorEnabled: boolean,
    credType: CredReqType
  ) {
    this.hasEntityHoldNextCredDate = !!hasEntityHoldNextCredDate;
    this.hasNoMonitorEnabled = !!hasNoMonitorEnabled;
    this.credType = credType;
    this.disableAllMonitorCheck = !this.hasNoMonitorEnabled;
    this.removeNextCredDateCheck = hasEntityHoldNextCredDate;
  }

  public get selectedStatusLabel(): string {
    return (
      (this.status && CredReqEventStatusExtension.get(this.status).label) || 'Select CVO Status'
    );
  }

  public get showNote(): boolean {
    return CredReqEventStatusExtension.getNotesSupportedStatusList().includes(this.status);
  }

  public get isCompletedStatus(): boolean {
    return CredReqEventStatusExtension.getCompletedCvoStatusList().includes(this.status);
  }

  public get isCancelledStatus(): boolean {
    return this.status === CredReqEventStatus.Cancelled;
  }

  public get removeNextCredDate(): boolean {
    return this.isCancelledStatus && this.removeNextCredDateCheck;
  }

  public get allowRemoveMonitors(): boolean {
    return this.isCancelledStatus && !!this.disableAllMonitorCheck;
  }

  public get allowNextCredentialDate(): boolean {
    return this.isCompletedStatus && !this.isOffCycleCredType;
  }

  public abstract get monitorCheckLabel(): string;

  public setStatus(item: CommonList): void {
    this.status = item.id as CredReqEventStatus;

    // Set next credentialing date to three years from today if the status is completed
    // and the credentialing type is not off-cycle. Otherwise, clear the next credentialing date.
    if (this.allowNextCredentialDate) this.setNextCredDateThreeYearFromToday();
    else this.setNextCredentialingDate(null);
  }

  public setNote(note: string): void {
    this.note = note;
  }

  public setDisableAllMonitorCheck(checked: boolean): void {
    this.disableAllMonitorCheck = !!checked;
  }

  public setNextCredentialingDate(date: string | null): void {
    this.nextCredentialingDate = date;
  }

  public setNextCredDateThreeYearFromToday(): void {
    this.nextCredentialingDate = dayjs().add(3, 'years').format(DateFormat.DatePickerFormat);
  }

  public validate(): void {
    if (!this.status) throw new Error('Please select CVO Status');
    if (!this.isCompletedStatus || !this.allowToValidateNextCredentialDate()) return;

    if (!this.nextCredentialingDate) throw new Error('Next Credentialing Date is required');

    const date = dayjs(this.nextCredentialingDate);
    if (date.isSameOrBefore(dayjs()))
      throw new Error('Next credentialing date cannot be set to today or earlier.');
  }

  protected get isOffCycleCredType(): boolean {
    return this.credType === CredReqType.OffCycle;
  }

  private allowToValidateNextCredentialDate(): boolean {
    return this.allowNextCredentialDate;
  }

  static getIngoredCredStatuses(): CredReqEventStatus[] {
    return [CredReqEventStatus.RequestSubmitted, CredReqEventStatus.CancellationRequested];
  }
}
